import React, { useState } from "react";
import { downloadMyData, deleteMyAccount, updateConsent } from "../api/client.js";

export default function CrmGdprPage() {
  const [deleteForm, setDeleteForm] = useState({ confirmEmail: "", reason: "" });
  const [consentForm, setConsentForm] = useState({
    marketingEmails: true,
    dataProcessing: true,
    thirdPartySharing: false
  });
  const [loading, setLoading] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleDownloadData = async () => {
    setLoading((prev) => ({ ...prev, download: true }));
    setError("");
    setSuccess("");
    try {
      const response = await downloadMyData();
      // Assuming API returns JSON data export
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `my-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
      setSuccess("Your data has been downloaded.");
    } catch (err) {
      setError(err.message || "Data download failed");
    } finally {
      setLoading((prev) => ({ ...prev, download: false }));
    }
  };

  const handleDeleteAccount = async (event) => {
    event.preventDefault();
    setLoading((prev) => ({ ...prev, delete: true }));
    setError("");
    setSuccess("");
    try {
      await deleteMyAccount(deleteForm);
      setSuccess("Account deletion request submitted. You will be logged out shortly.");
      setTimeout(() => {
        window.location.href = "/";
      }, 3000);
    } catch (err) {
      setError(err.message || "Account deletion failed");
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const handleUpdateConsent = async (event) => {
    event.preventDefault();
    setLoading((prev) => ({ ...prev, consent: true }));
    setError("");
    setSuccess("");
    try {
      await updateConsent(consentForm);
      setSuccess("Consent preferences updated successfully.");
    } catch (err) {
      setError(err.message || "Consent update failed");
    } finally {
      setLoading((prev) => ({ ...prev, consent: false }));
    }
  };

  return (
    <div className="space-y-6">
      <header className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Data privacy</p>
        <h1 className="text-3xl font-semibold text-white">GDPR Tools</h1>
        <p className="text-sm text-white/70">
          Manage your data rights including access, consent, and deletion under GDPR.
        </p>
      </header>

      {error && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
          {success}
        </div>
      )}

      <div className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Data portability</p>
          <h2 className="text-xl font-semibold text-white mt-2">Download your data</h2>
          <p className="text-sm text-white/70 mt-1">
            Export all your personal data stored in HOME AI as a JSON file. This includes your profile, saved searches, viewings, and offers.
          </p>
        </div>
        <button
          onClick={handleDownloadData}
          disabled={loading.download}
          className="rounded-xl border border-emerald-400/50 bg-emerald-500/10 px-5 py-3 text-sm text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50"
        >
          {loading.download ? "Preparing download…" : "Download my data"}
        </button>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Consent management</p>
          <h2 className="text-xl font-semibold text-white mt-2">Update consent preferences</h2>
          <p className="text-sm text-white/70 mt-1">
            Control how we use your data and communications.
          </p>
        </div>
        <form onSubmit={handleUpdateConsent} className="space-y-3">
          <label className="flex items-center gap-3 text-sm text-white">
            <input
              type="checkbox"
              checked={consentForm.marketingEmails}
              onChange={(event) =>
                setConsentForm((prev) => ({ ...prev, marketingEmails: event.target.checked }))
              }
              className="h-4 w-4 rounded border-white/20 bg-black/40 text-emerald-500 focus:ring-emerald-400"
            />
            Receive marketing emails and property alerts
          </label>
          <label className="flex items-center gap-3 text-sm text-white">
            <input
              type="checkbox"
              checked={consentForm.dataProcessing}
              onChange={(event) =>
                setConsentForm((prev) => ({ ...prev, dataProcessing: event.target.checked }))
              }
              className="h-4 w-4 rounded border-white/20 bg-black/40 text-emerald-500 focus:ring-emerald-400"
            />
            Allow processing of data for service improvement
          </label>
          <label className="flex items-center gap-3 text-sm text-white">
            <input
              type="checkbox"
              checked={consentForm.thirdPartySharing}
              onChange={(event) =>
                setConsentForm((prev) => ({ ...prev, thirdPartySharing: event.target.checked }))
              }
              className="h-4 w-4 rounded border-white/20 bg-black/40 text-emerald-500 focus:ring-emerald-400"
            />
            Share anonymized data with trusted third parties
          </label>
          <button
            type="submit"
            disabled={loading.consent}
            className="rounded-xl border border-sky-400/50 bg-sky-500/10 px-5 py-3 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
          >
            {loading.consent ? "Updating…" : "Update preferences"}
          </button>
        </form>
      </div>

      <div className="rounded-3xl border border-rose-500/40 bg-rose-500/10 p-6 space-y-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-rose-300">Right to erasure</p>
          <h2 className="text-xl font-semibold text-white mt-2">Delete your account</h2>
          <p className="text-sm text-rose-100 mt-1">
            ⚠ This action is permanent and cannot be undone. All your data will be deleted within 30 days.
          </p>
        </div>
        <form onSubmit={handleDeleteAccount} className="space-y-3">
          <label className="text-xs text-rose-200">
            Confirm your email
            <input
              type="email"
              required
              className="mt-2 w-full rounded-xl border border-rose-400/30 bg-black/40 px-3 py-2 text-sm text-white"
              value={deleteForm.confirmEmail}
              onChange={(event) =>
                setDeleteForm((prev) => ({ ...prev, confirmEmail: event.target.value }))
              }
              placeholder="your.email@example.com"
            />
          </label>
          <label className="text-xs text-rose-200">
            Reason for deletion (optional)
            <textarea
              className="mt-2 w-full rounded-xl border border-rose-400/30 bg-black/40 px-3 py-2 text-sm text-white"
              rows={3}
              value={deleteForm.reason}
              onChange={(event) =>
                setDeleteForm((prev) => ({ ...prev, reason: event.target.value }))
              }
              placeholder="Let us know why you're leaving"
            />
          </label>
          <button
            type="submit"
            disabled={loading.delete}
            className="rounded-xl border border-rose-500/50 bg-rose-600/20 px-5 py-3 text-sm text-rose-200 hover:bg-rose-600/30 disabled:opacity-50"
          >
            {loading.delete ? "Processing…" : "Delete my account permanently"}
          </button>
        </form>
      </div>
    </div>
  );
}
